import { from, of, Observable, isObservable } from 'rxjs'

export function isPromise(obj: any): obj is Promise<any> {
  // allow any Promise/A+ compliant thenable.
  // It's up to the caller to ensure that obj.then conforms to the spec
  return !!obj && typeof obj.then === 'function';
}

export function wrapIntoObservable<T>(value: T | Promise<T> | Observable<T>) {
  if (isObservable(value)) {
    return value;
  }

  if (isPromise(value)) {
    return from(Promise.resolve(value));
  }

  return of(value);
}

/**
 * Returns the value of a property from an object or the provided default
 */
export function propertyOrDefault<T, K extends keyof T>(obj: T, key: K, defaultValue: T[K]) {
  return prop(obj, key) || defaultValue;
}

/**
 * Gets a property from an object 
 */
export function prop<T, K extends keyof T>(obj: T, key: K) {
  return obj[key];
}

/** 
 returns the distinct values of property within an array 
*/
export function distinctProperty<T, TK extends keyof T>(array: Array<T>, property: TK) {
  return Array.from(new Set(array.map((item: any) => item[property])));
}

/**
 * returns the items from the array based distinct by property
 */
export function distinctByProperty<T, K extends keyof T>(array: Array<T>, property: K) {
  return array.filter((obj, pos, arr) => {
    return arr.map(mapObj => mapObj[property]).indexOf(obj[property]) === pos;
  });
}


export function sum(array: Array<any>, prop: string): number {
  if (array.length == 0)
    return 0;
  return array.map(y => typeof y[prop] == "number" ? y[prop] : 0)
    .reduce((sum, current) => sum + current);
}

export function groupByProp<T, K extends keyof T>(array: Array<T>, property: K): object {
  return groupBy(array, property);
}

export function groupBy<T>(array: Array<T>, prop): object {
  return array.reduce((groups, item) => {
    const val = item[prop];
    groups[val] = groups[val] || [];
    groups[val].push(item);
    return groups;
  },
    {});
}

export function groupAndSum<T, K extends keyof T>(arr: T[], groupKeys: K[], sumKeys: any[]){
  return Object.values(
    arr.reduce((acc,curr)=>{
      const group = groupKeys.map(k => curr[k]).join('-');
      acc[group] = acc[group] || Object.fromEntries(
         groupKeys.map(k => [k, curr[k]]).concat(sumKeys.map(k => [k, 0])));
      sumKeys.forEach(k => acc[group][k] += curr[k]);
      return acc;
    }, {})
  );
}

export function distinct<T, S>(array: Array<S>, prop): Array<T> {
  const allItems = array.map(item => item[prop]);

  return Array.from(new Set(allItems));
}

export function removeElementsFromArray<T>(array: Array<T>, elementsToRemove: Array<T>): Array<T> {
  return array.filter(el => elementsToRemove.indexOf(el) < 0);
}

export function distinctMap<T, S>(array: Array<S>, mapFunc: (item: S) => T): Array<T> {
  const allItems = array.map(item => mapFunc(item));
  return Array.from(new Set(allItems));
}

export function distinctMapObj<A, L>(array: Array<A>, mapFunc: (item: A) => L, keyFunc: (item: L) => any): Array<L> {
  const allItems = array.map(item => mapFunc(item));

  const distinctItemsIterator = function* (array: L[]) {
    const alreadySeen = new Set();
    for (let item of array) {
      if (!alreadySeen.has(keyFunc(item))) {
        alreadySeen.add(keyFunc(item));
        yield item;
      }
    }
  }
  return Array.from(distinctItemsIterator(allItems));
}


/**
 * given a string returns a css safe name with default prefix of "_"
 * @param name
 * @param prefix
 */
export function safeCssName(name: string, prefix: string = '_') {
  const words = name.replace(' ', '_');
  const result = words.replace(/[!"#$%&'()\*\+,\./:;<=>\?@\[\\\]‌​;^`{\|}~ ]+/g, '').toLowerCase();
  return prefix ? `${prefix}${result}` : result;
}

export function objectHasPropertyFrom(obj: any, props: string[]) {
  return Object.keys(obj).some(x => props.indexOf(x) > -1);
}


export function precisionRound(number, precision) {
  const factor = Math.pow(10, precision);
  return Math.round(number * factor) / factor;
}
